import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FaMapMarkerAlt, FaSearch, FaTimes, FaGlobeAmericas } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { AppDispatch } from '../../redux/Store';
import {
  searchLocations,
  getLocationById,
  updateHomeLocation,
  updateTouringLocation,
  Location,
} from '../../redux/thunks/locationThunks';
import { fetchUserPreferences, updateUserPreferences } from '../../redux/thunks/userPreferencesThunks';
import { fetchLocationFeed } from '../../redux/thunks/dashboardThunks';
import { resetFeedOffset, setLocationUnit } from '../../redux/slices/dashboardSlice';
import { useAppSelector } from '../../redux/Store';
import {
  SettingsPanel,
  SettingsRow,
  SettingsSaveButton,
  SettingsNote,
  SettingsSaveStatus,
} from './primitives';
import './LocationSettings.scss';

const formatLocationText = (location: Location): string => {
  const parts = [location.city, location.admin1, location.countryCode].filter(Boolean);
  return parts.join(', ');
};

interface LocationPickerProps {
  selected: Location | null;
  searchQuery: string;
  searchResults: Location[];
  searching: boolean;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  onSearch: (query: string) => void;
  onSelect: (location: Location) => void;
  onClear?: () => void;
  selectedIcon: React.ReactNode;
  placeholder: string;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  selected,
  searchQuery,
  searchResults,
  searching,
  dropdownOpen,
  setDropdownOpen,
  onSearch,
  onSelect,
  onClear,
  selectedIcon,
  placeholder,
}) => {
  if (selected) {
    return (
      <div className="location-selected">
        <div className="location-selected__text">
          {selectedIcon}
          <span>{formatLocationText(selected)}</span>
        </div>
        <div className="location-selected__actions">
          <button type="button" className="location-change-btn" onClick={() => onSearch('')}>
            Change
          </button>
          {onClear && (
            <button type="button" className="location-clear-btn" onClick={onClear}>
              <FaTimes /> Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="location-search">
      <div className="location-search__input">
        <FaSearch className="location-search__icon" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          onFocus={() => setDropdownOpen(true)}
        />
        {searching && <span className="location-search__spinner">Searching…</span>}
      </div>

      {dropdownOpen && searchResults.length > 0 && (
        <div className="location-search__results">
          {searchResults.map((location) => (
            <button
              key={location.id}
              type="button"
              className="location-search__result"
              onClick={() => onSelect(location)}
            >
              <FaMapMarkerAlt />
              <span>{formatLocationText(location)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const LocationSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const locationRadius = useAppSelector((state) => state.dashboard.locationRadius);
  const locationUnit = useAppSelector((state) => state.dashboard.locationUnit);
  const activeFeedTab = useAppSelector((state) => state.dashboard.activeFeedTab);

  const [homeLocation, setHomeLocation] = useState<Location | null>(null);
  const [homeSearchQuery, setHomeSearchQuery] = useState('');
  const [homeSearchResults, setHomeSearchResults] = useState<Location[]>([]);
  const [homeSearching, setHomeSearching] = useState(false);
  const [homeDropdownOpen, setHomeDropdownOpen] = useState(false);

  const [touringLocation, setTouringLocation] = useState<Location | null>(null);
  const [touringSearchQuery, setTouringSearchQuery] = useState('');
  const [touringSearchResults, setTouringSearchResults] = useState<Location[]>([]);
  const [touringSearching, setTouringSearching] = useState(false);
  const [touringDropdownOpen, setTouringDropdownOpen] = useState(false);

  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadCurrentLocations();
  }, []);

  const loadCurrentLocations = async () => {
    try {
      const preferences = await dispatch(fetchUserPreferences()).unwrap();
      // The unit pill reads from dashboard.locationUnit. That slice has
      // no rehydration of its own, so without this dispatch a refresh
      // snaps the UI back to the slice's 'miles' default regardless of
      // what's actually persisted on the server.
      if (preferences.distanceUnit === 'miles' || preferences.distanceUnit === 'km') {
        dispatch(setLocationUnit(preferences.distanceUnit));
      }
      if (preferences.homeId) {
        const home = await dispatch(getLocationById(preferences.homeId)).unwrap();
        setHomeLocation(home);
      }
      if (preferences.touringId) {
        const touring = await dispatch(getLocationById(preferences.touringId)).unwrap();
        setTouringLocation(touring);
      }
    } catch (error) {
      console.log('Could not load current locations');
    }
  };

  const handleHomeSearch = useCallback(
    async (query: string) => {
      setHomeSearchQuery(query);
      setHomeDropdownOpen(true);
      if (query.trim().length < 2) {
        setHomeSearchResults([]);
        return;
      }
      setHomeSearching(true);
      try {
        const result = await dispatch(searchLocations({ query, limit: 10 })).unwrap();
        setHomeSearchResults(result.locations);
      } catch (error) {
        console.error('Home location search failed:', error);
        setHomeSearchResults([]);
      } finally {
        setHomeSearching(false);
      }
    },
    [dispatch]
  );

  const handleTouringSearch = useCallback(
    async (query: string) => {
      setTouringSearchQuery(query);
      setTouringDropdownOpen(true);
      if (query.trim().length < 2) {
        setTouringSearchResults([]);
        return;
      }
      setTouringSearching(true);
      try {
        const result = await dispatch(searchLocations({ query, limit: 10 })).unwrap();
        setTouringSearchResults(result.locations);
      } catch (error) {
        console.error('Touring location search failed:', error);
        setTouringSearchResults([]);
      } finally {
        setTouringSearching(false);
      }
    },
    [dispatch]
  );

  const handleDistanceUnitChange = async (unit: 'miles' | 'km') => {
    // `updateUserPreferences` persists to the user-preferences slice +
    // server, but the unit pill's active state reads from
    // `state.dashboard.locationUnit` (the dashboard slice owns the
    // selector that drives both this UI and the location feed). The
    // two slices aren't synced, so without the explicit dispatch the
    // button never visually toggles. Update both.
    dispatch(setLocationUnit(unit));
    try {
      await dispatch(updateUserPreferences({ distanceUnit: unit })).unwrap();
      toast.success(`Distance unit set to ${unit}`);
      if (activeFeedTab === 'location') {
        dispatch(resetFeedOffset());
        dispatch(fetchLocationFeed({ limit: 20, offset: 0, append: false, radius: locationRadius, unit }));
      }
    } catch (error) {
      console.error('Failed to update distance unit:', error);
      toast.error('Failed to save distance unit');
    }
  };

  const handleSave = async () => {
    if (!homeLocation) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }
    setSaveStatus('saving');
    try {
      await dispatch(updateHomeLocation(homeLocation.id)).unwrap();
      await dispatch(updateTouringLocation(touringLocation?.id || null)).unwrap();
      if (activeFeedTab === 'location') {
        dispatch(resetFeedOffset());
        dispatch(fetchLocationFeed({ limit: 20, offset: 0, append: false, radius: locationRadius, unit: locationUnit }));
      }
      setSaveStatus('saved');
      setHasChanges(false);
      toast.success('Locations saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save locations:', error);
      setSaveStatus('error');
      toast.error('Failed to save locations');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  return (
    <SettingsPanel
      title="Location Settings"
      description="Set your home location and optional touring location"
    >
      <SettingsRow
        icon={<FaMapMarkerAlt />}
        title="Home Location"
        description="Your primary location (required)"
      >
        <LocationPicker
          selected={homeLocation}
          searchQuery={homeSearchQuery}
          searchResults={homeSearchResults}
          searching={homeSearching}
          dropdownOpen={homeDropdownOpen}
          setDropdownOpen={setHomeDropdownOpen}
          onSearch={(query) => {
            if (homeLocation) {
              setHomeLocation(null);
              setHasChanges(true);
            }
            handleHomeSearch(query);
          }}
          onSelect={(location) => {
            setHomeLocation(location);
            setHomeSearchQuery('');
            setHomeSearchResults([]);
            setHomeDropdownOpen(false);
            setHasChanges(true);
          }}
          selectedIcon={<FaMapMarkerAlt className="location-selected__icon" />}
          placeholder="Search for city, state, or postal code…"
        />
      </SettingsRow>

      <SettingsRow
        icon={<FaGlobeAmericas />}
        title="Touring Location"
        description="Temporary location when traveling (optional)"
      >
        <LocationPicker
          selected={touringLocation}
          searchQuery={touringSearchQuery}
          searchResults={touringSearchResults}
          searching={touringSearching}
          dropdownOpen={touringDropdownOpen}
          setDropdownOpen={setTouringDropdownOpen}
          onSearch={(query) => {
            if (touringLocation) {
              setTouringLocation(null);
              setHasChanges(true);
            }
            handleTouringSearch(query);
          }}
          onSelect={(location) => {
            setTouringLocation(location);
            setTouringSearchQuery('');
            setTouringSearchResults([]);
            setTouringDropdownOpen(false);
            setHasChanges(true);
          }}
          onClear={() => {
            setTouringLocation(null);
            setTouringSearchQuery('');
            setTouringSearchResults([]);
            setTouringDropdownOpen(false);
            setHasChanges(true);
          }}
          selectedIcon={<FaGlobeAmericas className="location-selected__icon" />}
          placeholder="Search for touring location…"
        />
      </SettingsRow>

      <SettingsRow
        icon={<FaMapMarkerAlt />}
        title="Distance Unit"
        description="Choose your preferred unit for distance measurements"
        control={
          <div className="distance-unit-selector">
            <button
              type="button"
              className={`distance-unit-btn ${locationUnit === 'miles' ? 'active' : ''}`}
              onClick={() => handleDistanceUnitChange('miles')}
            >
              Miles
            </button>
            <button
              type="button"
              className={`distance-unit-btn ${locationUnit === 'km' ? 'active' : ''}`}
              onClick={() => handleDistanceUnitChange('km')}
            >
              Kilometers
            </button>
          </div>
        }
      />

      <SettingsNote tone="info">
        <p>
          <strong>Note:</strong> Your touring location takes priority over your home location for
          location-based feeds. Clear your touring location to return to your home location.
        </p>
      </SettingsNote>

      <SettingsSaveButton
        status={saveStatus}
        onClick={handleSave}
        disabled={!hasChanges || !homeLocation}
        idleLabel={hasChanges ? 'Save Locations' : 'No Changes'}
      />
    </SettingsPanel>
  );
};

export default LocationSettings;
